import { defineStore } from 'pinia'
import { ref } from 'vue'
import Network from '@/Network'
import { getApiErrorMessage } from '@/utils/apiError'
import type { Sector } from '@/Network/Types/Responses/SectorResponses'
import { isRefArchived } from '@/utils/stationDisplay'

export const SECTORS_LOAD_ERROR = 'Não foi possível carregar os setores'

const byName = (a: Sector, b: Sector) =>
  a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' })

export const useSectorStore = defineStore('sector', () => {
  const byUnit = ref<Record<number, Sector[]>>({})
  const suggestions = ref<string[]>([])
  const suggestionsLoaded = ref(false)
  const suggestionsError = ref<string | null>(null)
  const loadErrorByUnit = ref<Record<number, string | null>>({})
  const inFlight = new Map<number, Promise<Sector[]>>()

  const sectorsOf = (unitId: number | null | undefined): Sector[] =>
    unitId ? (byUnit.value[unitId] ?? []) : []

  const activeSectorsOf = (unitId: number | null | undefined): Sector[] =>
    sectorsOf(unitId)
      .filter((sector) => !isRefArchived(sector))
      .sort(byName)

  function sectorName(sectorId: number): string | null {
    for (const list of Object.values(byUnit.value)) {
      const found = list.find((sector) => sector.id === sectorId)
      if (found) return found.name
    }
    return null
  }

  async function fetchByHealthUnit(
    unitId: number,
    options: { force?: boolean } = {}
  ): Promise<Sector[]> {
    if (!options.force && byUnit.value[unitId]) return byUnit.value[unitId]
    const pending = inFlight.get(unitId)
    if (pending && !options.force) return pending
    // Resposta de uma request substituída (force/$reset) é obsoleta: não grava nem limpa a nova.
    const isCurrent = () => inFlight.get(unitId) === request
    const request: Promise<Sector[]> = (async () => {
      try {
        const sectors = await Network.sector.listByHealthUnit(unitId, { includeArchived: true })
        if (!isCurrent()) return sectorsOf(unitId)
        byUnit.value = { ...byUnit.value, [unitId]: sectors }
        loadErrorByUnit.value = { ...loadErrorByUnit.value, [unitId]: null }
        return sectors
      } catch (error) {
        if (!isCurrent()) return sectorsOf(unitId)
        // fallback intencional: combobox/filtro seguem sem opções e mostram loadErrorByUnit.
        loadErrorByUnit.value = {
          ...loadErrorByUnit.value,
          [unitId]: getApiErrorMessage(error, SECTORS_LOAD_ERROR)
        }
        return []
      } finally {
        if (isCurrent()) inFlight.delete(unitId)
      }
    })()
    inFlight.set(unitId, request)
    return request
  }

  async function fetchSuggestions(): Promise<string[]> {
    if (suggestionsLoaded.value) return suggestions.value
    try {
      suggestionsError.value = null
      suggestions.value = await Network.sector.suggestions()
      suggestionsLoaded.value = true
    } catch (error) {
      // fallback intencional: sem sugestões o cadastro continua; o painel mostra suggestionsError.
      suggestionsError.value = getApiErrorMessage(
        error,
        'Não foi possível carregar as sugestões de setor'
      )
    }
    return suggestions.value
  }

  function upsert(unitId: number, sector: Sector): void {
    const list = sectorsOf(unitId)
    const exists = list.some((item) => item.id === sector.id)
    byUnit.value = {
      ...byUnit.value,
      [unitId]: exists
        ? list.map((item) => (item.id === sector.id ? sector : item))
        : [...list, sector]
    }
  }

  async function createSector(unitId: number, name: string): Promise<Sector> {
    const sector = await Network.sector.create(unitId, { name: name.trim() })
    upsert(unitId, sector)
    return sector
  }

  async function renameSector(unitId: number, sectorId: number, name: string): Promise<Sector> {
    const sector = await Network.sector.update(sectorId, { name: name.trim() })
    upsert(unitId, sector)
    return sector
  }

  async function archiveSector(unitId: number, sectorId: number): Promise<Sector> {
    const sector = await Network.sector.archive(sectorId)
    upsert(unitId, sector)
    return sector
  }

  async function unarchiveSector(unitId: number, sectorId: number): Promise<Sector> {
    const sector = await Network.sector.unarchive(sectorId)
    upsert(unitId, sector)
    return sector
  }

  async function deleteSector(unitId: number, sectorId: number): Promise<void> {
    await Network.sector.remove(sectorId)
    byUnit.value = {
      ...byUnit.value,
      [unitId]: sectorsOf(unitId).filter((sector) => sector.id !== sectorId)
    }
  }

  function $reset(): void {
    byUnit.value = {}
    suggestions.value = []
    suggestionsLoaded.value = false
    suggestionsError.value = null
    loadErrorByUnit.value = {}
    inFlight.clear()
  }

  return {
    byUnit,
    suggestions,
    suggestionsError,
    loadErrorByUnit,
    sectorsOf,
    activeSectorsOf,
    sectorName,
    fetchByHealthUnit,
    fetchSuggestions,
    createSector,
    renameSector,
    archiveSector,
    unarchiveSector,
    deleteSector,
    $reset
  }
})
